import { getOspreyApiBaseUrl } from '../../../shared/utils/apiBase';
import { useAuthStore } from '../store';

/**
 * TOTP two-factor authentication (PE-2157, backend PE-2156 / watchman #99).
 *
 * The routes live on watchman and reach us through osprey-api's `iam` catch-all
 * (`@Controller('iam')` + `@All('*')`), which forwards `/api/v1/iam/<rest>` to
 * watchman `/api/v1/<rest>` verbatim. They are therefore INVISIBLE in the pulled
 * osprey.openapi.json — nothing matching `2fa` appears there. Verified against
 * watchman source at origin/development:
 *   src/domain/two-factor/controllers/two-factor.controller.ts   @Controller() —
 *     POST 2fa/enroll · POST 2fa/confirm · POST 2fa/disable · POST users/:id/2fa/reset
 *     (all Bearer-gated — this client)
 *   src/domain/authentication/controllers/authentication.controller.ts —
 *     POST auth/2fa/verify · POST auth/2fa/enroll · POST auth/2fa/enroll/confirm
 *     (all @Public, authenticated by a pendingToken — these live in authApiClient)
 *
 * ⚠️ `2fa/enroll` and `auth/2fa/enroll` are DIFFERENT ENDPOINTS on different
 * controllers with different auth. The `/auth`-prefixed pair is the signed-out
 * bootstrap path added by watchman #102; the bare pair is self-service for an
 * account that already has a session. Reaching for the wrong one is a 401.
 *
 * ⚠️ Deliberately NOT using ApiFetch or WatchmanFetch. Both of them:
 *   1. treat ANY 401 as an expired session — they call logout() and hard-redirect
 *      to /login. Here a 401 is the ordinary "wrong code" answer, so routing 2FA
 *      through them would sign the user out for a typo.
 *   2. end unconditionally in `res.json()`, which throws on the 204 No Content
 *      returned by /2fa/disable and /users/:id/2fa/reset.
 * Both behaviours are correct for the rest of the app, so this client is the
 * exception rather than a change to the shared ones.
 */

const iamBase = (): string => getOspreyApiBaseUrl() + '/iam';

/** Login rejected the password/Google credentials: 2FA is on, finish with verify(). */
export const TWO_FACTOR_REQUIRED = 'TWO_FACTOR_REQUIRED';

/**
 * Login rejected the credentials because 2FA is MANDATORY for this account's
 * role/merchant policy but was never enrolled. `isRequired()` mandates 2FA for
 * every COLLECTIVE_SUPERADMIN and COLLECTIVE_ADMIN, plus MERCHANT_ADMIN at
 * merchants with `require2faForAdmins`.
 *
 * This WAS an unrecoverable lockout — watchman threw it before issuing any
 * token and with no `pendingToken`, while `POST /2fa/enroll` below sat behind
 * the global AuthGuard, so the account could never obtain the credential its
 * own enrolment required (PE-2229).
 *
 * watchman #102 broke that deadlock: the 401 now carries a `pendingToken`, and
 * two `@Public()` routes accept it in place of a Bearer token —
 * `POST /auth/2fa/enroll` and `POST /auth/2fa/enroll/confirm`, wrapped by
 * `authApiClient.startEnrollment` / `completeEnrollment`. Those live on the
 * authentication controller, NOT on the two-factor controller this client
 * otherwise talks to; the `/auth` prefix is the whole difference between the
 * reachable route and the deadlocked one.
 *
 * The signed-out enrolment screen is `TwoFactorEnrollmentForm`. LoginForm still
 * keeps the old explanatory dead-end for a 401 that arrives WITHOUT a
 * pendingToken, so an environment running pre-#102 watchman degrades to the old
 * message instead of showing a flow that cannot complete.
 */
export const TWO_FACTOR_ENROLLMENT_REQUIRED = 'TWO_FACTOR_ENROLLMENT_REQUIRED';

export interface TwoFactorEnrollment {
  secret: string;
  otpauthUri: string;
  /** data: URI PNG of the otpauth URI — render directly in an <img>. */
  qrDataUri: string;
}

export interface TwoFactorError {
  status: number;
  code?: string;
  message: string;
  pendingToken?: string;
}

async function toError(res: Response, fallback: string): Promise<TwoFactorError> {
  const body = await res.json().catch(() => ({}) as Record<string, unknown>);
  const raw = (body as { message?: unknown }).message;
  const message = Array.isArray(raw)
    ? ((raw as string[])[0] ?? fallback)
    : ((raw as string) || res.statusText || fallback);
  return {
    status: res.status,
    code: (body as { code?: string }).code,
    message,
    pendingToken: (body as { pendingToken?: string }).pendingToken,
  };
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  const token = useAuthStore.getState().token;
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export const twoFactorApiClient = {
  /**
   * Starts enrolment. The secret is held server-side for 10 minutes and is NOT
   * active until confirm() succeeds, so abandoning this screen leaves 2FA off.
   */
  enroll: async (): Promise<TwoFactorEnrollment> => {
    const res = await fetch(`${iamBase()}/2fa/enroll`, { method: 'POST', headers: authHeaders() });
    if (!res.ok) throw await toError(res, 'Could not start two-factor setup');
    return res.json();
  },

  /**
   * Activates 2FA and returns 10 single-use backup codes. The codes are shown
   * exactly once — the caller must not discard them without the user confirming
   * they were saved. A 401 here means the code was wrong or the 10-minute
   * enrolment window lapsed; the caller re-runs enroll() for a fresh QR.
   */
  confirm: async (code: string): Promise<{ backupCodes: string[] }> => {
    const res = await fetch(`${iamBase()}/2fa/confirm`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ code }),
    });
    if (!res.ok) throw await toError(res, 'That code was not accepted');
    return res.json();
  },

  /** Turns 2FA off and destroys the remaining backup codes. 204 — no body. */
  disable: async (code: string): Promise<void> => {
    const res = await fetch(`${iamBase()}/2fa/disable`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ code }),
    });
    if (!res.ok) throw await toError(res, 'That code was not accepted');
  },

  /**
   * Clears another user's 2FA so they can re-enrol — the recovery path for a
   * lost authenticator. TCS staff only (watchman requires USER.UPDATE *and*
   * COLLECTIVE scope). 204 — no body.
   */
  adminReset: async (userId: string): Promise<void> => {
    const res = await fetch(`${iamBase()}/users/${userId}/2fa/reset`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!res.ok) throw await toError(res, 'Could not reset two-factor for this user');
  },
};
